"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { createPaymentLocalisedSchema, PromoCodeSchema } from "@/lib/validation/payment";
import { useCheckoutStore } from "@/stores/checkout-store";
import { Button } from "@/components/atoms/buttons";
import { __ } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { useCart } from "@/hooks/useCart";
import { sdk } from "@/lib/medusa/client";
import { toast } from "@/components/atoms/toast";
import { memo, useEffect, useTransition } from "react";
import { Loader2 } from "lucide-react";
import { StoreCart } from "@medusajs/types";

type PromoCodeFormProps = {
  locale: Locales;
  className?: string;
};

const PromoCodeForm = ({ locale, className }: PromoCodeFormProps) => {
  const { promoCodeOnlySchema } = createPaymentLocalisedSchema(locale);
  const setData = useCheckoutStore((state) => state.setData);
  const promoCodeStored = useCheckoutStore((state) => state.promoCode);
  const [isPending, startTransition] = useTransition();
  const { cart, setCart } = useCart();

  const promoCodeInCart = cart?.promotions?.[0]?.code ?? "";
  const cartHasLoaded = typeof cart !== "undefined" && typeof cart?.id === "string";
  const isSuccessful = cartHasLoaded && promoCodeInCart.length > 0;

  const currentPromoCode = promoCodeStored || promoCodeInCart || "";

  const form = useForm<PromoCodeSchema>({
    resolver: zodResolver(promoCodeOnlySchema),
    defaultValues: {
      promoCode: currentPromoCode,
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!cartHasLoaded) return;

    reset({ promoCode: currentPromoCode });
  }, [cartHasLoaded, currentPromoCode, reset]); // Reset form when cart loads or promo code changes like when clearing the entire cart

  const removePromoCode = async () => {
    if (!cart?.id || !promoCodeStored) return;
    startTransition(async () => {
      try {
        const res = (await sdk.client.fetch(`store/carts/${cart.id}/promotions`, {
          method: "DELETE",
          body: {
            promo_codes: [promoCodeStored],
          },
        })) as { cart: StoreCart };
        setCart(res.cart);
        setData({ promoCode: "" });
        reset({ promoCode: "" });
        toast.success({ title: __("Promo code removed successfully", locale) });
      } catch (error) {
        console.error("Error removing promo code:", error);
        toast.error({ title: __("Failed to remove promo code", locale) });
      }
    });
  };

  const onSubmit = async (data: PromoCodeSchema) => {
    if (!cart?.id) return;

    if (isSuccessful) {
      await removePromoCode();
      return;
    } // Already applied → Remove it

    // CASE : NO ITEMS IN CART AND CUSTOMER TRIES TO APPLY PROMO CODE
    if (!cart.items?.[0]) {
      toast.error({ title: __("Add products to apply promo code", locale) });
      return;
    }

    // Apply promo code
    startTransition(async () => {
      // CASE : APPLYING PROMO CODE
      const res = (await sdk.client.fetch(`store/carts/${cart?.id}/promotions`, {
        method: "POST",
        body: {
          promo_codes: [data.promoCode],
        },
      })) as { cart: StoreCart };

      // Check if the promo code was applied successfully
      if (res.cart.promotions[0]) {
        setCart(res.cart);
        reset({ promoCode: data.promoCode });
        setData({ promoCode: data.promoCode }); // Persist to storage
        toast.success({ title: __("Promo code applied successfully", locale) });
        return;
      }

      // If we reach here, the promo code was invalid
      toast.error({ title: __("Invalid promo code", locale) });
      reset({ promoCode: "" }); // Reset form if promo code is invalid
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn("border-primary-light/50 border-t pt-5", className)}>
        <FormField
          control={control}
          name="promoCode"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-3.5 sm:flex-row">
              <FormLabel className={cn("mb-0 flex-1 text-base font-medium", errors.promoCode && "sm:-mt-6")}>
                {__("A promo code?", locale)}
              </FormLabel>
              <FormControl className="w-full">
                <div className="space-y-1.5 sm:max-w-[313px]">
                  <div className="relative">
                    <Input
                      disabled={isPending || isSuccessful}
                      type="text"
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[52px] flex-1 pr-24 placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                      placeholder={__("Enter your promo code", locale)}
                      {...field}
                    />
                    <Button
                      className="text-dark-primary absolute top-1.5 right-1.5 h-10 rounded-sm bg-[#8080801F] text-sm font-medium"
                      size="md"
                      type="submit"
                      variant="secondary"
                      disabled={!cartHasLoaded || isPending}
                    >
                      {isPending ? (
                        <Loader2 className="animate-spin" aria-hidden="true" />
                      ) : !isSuccessful ? (
                        __("Add", locale)
                      ) : (
                        __("Remove", locale)
                      )}
                    </Button>
                  </div>
                  <FormMessage />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default memo(PromoCodeForm);
